import { recordOriginalGeometryPositions } from './meshOriginalGeometry';
import { DEFAULT_INVISIBLE_MESH_TAG } from '../BaseModelLoader';
import {
  recomputeGeometryBounds,
  setVertexFromObjectLocal,
} from './objectSpaceGeometry';
import {isRuntimeCabinetHandle} from '../cabinetLoader/cabinetHandleRuntime';

const DEFAULT_SIDE_BAND_RATIO = 0.12;
const DEFAULT_FRONT_SIDE_BAND_RATIO = 0.28;
const DEFAULT_FRONT_MESH_NAME = /^(Door[ _](Left|Right)|Drawer)/;
const DOOR_MESH_NAME = /^Door[ _](Left|Right)/;
// Approximate central panel region in normalized door bounds; frame/profile UVs stay authored.
const DOOR_CENTER_PANEL_MIN_U = 0.22;
const DOOR_CENTER_PANEL_MAX_U = 0.78;
const DOOR_CENTER_PANEL_MIN_Y = 0.16;
const DOOR_CENTER_PANEL_MAX_Y = 0.84;
// Current material_url wood grain reads closer to the cabinet body when the panel projection is rotated.
const DOOR_CENTER_PANEL_ROTATE_TEXTURE = true;
// Larger than 1 reduces obvious repeated clones while still avoiding full-width texture blur.
const DOOR_CENTER_PANEL_TEXTURE_SCALE = 3;

function getPointCoordinate(point, axis) {
  return axis === 'x' ? point.x : point.y;
}

function getPointBounds(points) {
  const xCoordinates = points.map((point) => point.x);
  const yCoordinates = points.map((point) => point.y);

  return {
    minX: Math.min(...xCoordinates),
    maxX: Math.max(...xCoordinates),
    minY: Math.min(...yCoordinates),
    maxY: Math.max(...yCoordinates),
  };
}

function getModelCoordinate(point, axis, offsetBounds, scaleBounds, modelBounds) {
  const shapeMin = axis === 'x' ? offsetBounds.minX : offsetBounds.minY;
  const shapeMax = axis === 'x' ? scaleBounds.maxX : scaleBounds.maxY;
  const scaleMin = axis === 'x' ? scaleBounds.minX : scaleBounds.minY;
  const modelMin = axis === 'x' ? modelBounds.minX : modelBounds.minZ;
  const modelSize = axis === 'x' ? modelBounds.width : modelBounds.depth;
  const shapeSize = shapeMax - scaleMin;

  if (!shapeSize || shapeSize <= 0 || !modelSize || modelSize <= 0) {
    return modelMin;
  }

  const coordinate = getPointCoordinate(point, axis);
  return modelMin + (((coordinate - shapeMin) / shapeSize) * modelSize);
}

function getModelPoint(point, offsetBounds, scaleBounds, modelBounds) {
  return {
    x: getModelCoordinate(point, 'x', offsetBounds, scaleBounds, modelBounds),
    z: getModelCoordinate(point, 'y', offsetBounds, scaleBounds, modelBounds),
  };
}

function getVectorLength(vector) {
  return Math.sqrt((vector.x * vector.x) + (vector.z * vector.z));
}

function normalizeVector(vector) {
  const length = getVectorLength(vector);

  if (!length || length <= 0) {
    return null;
  }

  return {
    x: vector.x / length,
    z: vector.z / length,
  };
}

// One-dimensional 9-slice: preserve both ends of a front segment and distribute resize through the middle.
function getNineSliceDistance(value, sourceLength, targetLength, sideBandRatio) {
  if (!sourceLength || sourceLength <= 0 || !targetLength || targetLength <= 0) {
    return value;
  }

  const sourceBandSize = sourceLength * sideBandRatio;
  const targetBandSize = Math.min(sourceBandSize, targetLength * 0.45);
  const sourceEndBandStart = sourceLength - sourceBandSize;
  const targetEndBandStart = targetLength - targetBandSize;

  if (value <= sourceBandSize) {
    return value * (targetBandSize / sourceBandSize);
  }

  if (value >= sourceEndBandStart) {
    return targetLength - ((sourceLength - value) * (targetBandSize / sourceBandSize));
  }

  const sourceMiddleSize = sourceEndBandStart - sourceBandSize;
  const targetMiddleSize = targetEndBandStart - targetBandSize;

  if (sourceMiddleSize <= 0 || targetMiddleSize < 0) {
    return value;
  }

  const middleRatio = (value - sourceBandSize) / sourceMiddleSize;
  return targetBandSize + (middleRatio * targetMiddleSize);
}

// A front segment stores source and target lines in model space, plus directions for along-line projection.
function buildSegment(sourceFrom, sourceTo, targetFrom, targetTo) {
  const sourceVector = {
    x: sourceTo.x - sourceFrom.x,
    z: sourceTo.z - sourceFrom.z,
  };
  const targetVector = {
    x: targetTo.x - targetFrom.x,
    z: targetTo.z - targetFrom.z,
  };
  const sourceLength = getVectorLength(sourceVector);
  const targetLength = getVectorLength(targetVector);
  const sourceDirection = normalizeVector(sourceVector);
  const targetDirection = normalizeVector(targetVector);

  if (!sourceDirection || !targetDirection || sourceLength <= 0 || targetLength <= 0) {
    return null;
  }

  return {
    sourceFrom,
    targetFrom,
    sourceLength,
    targetLength,
    sourceDirection,
    targetDirection,
    sourceNormal: {
      x: -sourceDirection.z,
      z: sourceDirection.x,
    },
    targetNormal: {
      x: -targetDirection.z,
      z: targetDirection.x,
    },
  };
}

function getProjectedPointOnSegment(point, segment) {
  const relative = {
    x: point.x - segment.sourceFrom.x,
    z: point.z - segment.sourceFrom.z,
  };
  const distanceAlongSegment = (relative.x * segment.sourceDirection.x) + (relative.z * segment.sourceDirection.z);
  const clampedDistance = Math.max(0, Math.min(segment.sourceLength, distanceAlongSegment));
  const normalOffset = (relative.x * segment.sourceNormal.x) + (relative.z * segment.sourceNormal.z);
  const distanceFromSegment = Math.sqrt(
    Math.pow(relative.x - (segment.sourceDirection.x * clampedDistance), 2)
    + Math.pow(relative.z - (segment.sourceDirection.z * clampedDistance), 2)
  );

  return {
    clampedDistance,
    distanceFromSegment,
    normalOffset,
  };
}

function findClosestSegment(point, segments) {
  return segments.reduce((closest, segment) => {
    const projection = getProjectedPointOnSegment(point, segment);

    if (!closest || projection.distanceFromSegment < closest.projection.distanceFromSegment) {
      return {
        segment,
        projection,
      };
    }

    return closest;
  }, null);
}

function getResizedFrontPoint(point, segments, sideBandRatio) {
  const closest = findClosestSegment(point, segments);

  if (!closest) {
    return point;
  }

  const { segment, projection } = closest;
  const targetDistance = getNineSliceDistance(
    projection.clampedDistance,
    segment.sourceLength,
    segment.targetLength,
    sideBandRatio
  );

  return {
    x: segment.targetFrom.x
      + (segment.targetDirection.x * targetDistance)
      + (segment.targetNormal.x * projection.normalOffset),
    z: segment.targetFrom.z
      + (segment.targetDirection.z * targetDistance)
      + (segment.targetNormal.z * projection.normalOffset),
  };
}

// Converts 2D front lines into model-space segments so door/drawer meshes can follow edited cabinet fronts.
function buildFrontSegments(frontLineIndexes, originalPoints, currentPoints, modelBounds) {
  if (
    !Array.isArray(frontLineIndexes)
    || !Array.isArray(originalPoints)
    || !Array.isArray(currentPoints)
    || originalPoints.length !== currentPoints.length
    || originalPoints.length === 0
  ) {
    return [];
  }

  const originalBounds = getPointBounds(originalPoints);
  const currentBounds = getPointBounds(currentPoints);

  return frontLineIndexes
    .map((lineIndex) => {
      const fromIndex = lineIndex;
      const toIndex = (lineIndex + 1) % originalPoints.length;
      const sourceFrom = getModelPoint(originalPoints[fromIndex], originalBounds, originalBounds, modelBounds);
      const sourceTo = getModelPoint(originalPoints[toIndex], originalBounds, originalBounds, modelBounds);
      const targetFrom = getModelPoint(currentPoints[fromIndex], currentBounds, originalBounds, modelBounds);
      const targetTo = getModelPoint(currentPoints[toIndex], currentBounds, originalBounds, modelBounds);

      return buildSegment(sourceFrom, sourceTo, targetFrom, targetTo);
    })
    .filter(Boolean);
}

function isFrontMesh(mesh, frontMeshName) {
  let current = mesh;

  while (current && current.isObject3D) {
    if (current.name && frontMeshName.test(current.name)) {
      return true;
    }

    current = current.parent;
  }

  return false;
}

// Resizes a front mesh by projecting each original vertex to the nearest front segment and moving it on target segment.
function resizeFrontMesh(mesh, instance, segments, options) {
  const originalGeometry = recordOriginalGeometryPositions(mesh, options.geometryTag, null, null);

  if (!originalGeometry) {
    return;
  }

  mesh.updateMatrixWorld(true);

  const THREE = window.THREE;
  const vertex = new THREE.Vector3();
  const originalVertex = new THREE.Vector3();
  const currentVertex = new THREE.Vector3();
  const instanceLocalVertex = new THREE.Vector3();
  const resizedPoint = { x: 0, z: 0 };
  const position = mesh.geometry.attributes.position;

  for (let index = 0; index < originalGeometry.positions.length; index += 3) {
    originalVertex.set(
      originalGeometry.positions[index],
      originalGeometry.positions[index + 1],
      originalGeometry.positions[index + 2]
    );
    currentVertex.set(
      position.array[index],
      position.array[index + 1],
      position.array[index + 2]
    );

    mesh.localToWorld(originalVertex);
    instance.worldToLocal(originalVertex);
    mesh.localToWorld(currentVertex);
    instance.worldToLocal(currentVertex);

    Object.assign(resizedPoint, getResizedFrontPoint(
      { x: originalVertex.x, z: originalVertex.z },
      segments,
      options.sideBandRatio
    ));

    instanceLocalVertex.copy(currentVertex);
    instanceLocalVertex.x = resizedPoint.x;
    instanceLocalVertex.z = resizedPoint.z;

    setVertexFromObjectLocal(mesh, instance, vertex, instanceLocalVertex);

    position.array[index] = vertex.x;
    position.array[index + 1] = vertex.y;
    position.array[index + 2] = vertex.z;
  }

  position.needsUpdate = true;
  recomputeGeometryBounds(mesh.geometry);
}

function getPrimaryMaterial(material) {
  const sourceMaterial = Array.isArray(material) ? material[0] : material;
  return sourceMaterial || null;
}

function updateTextureWrapping(material) {
  const materials = Array.isArray(material) ? material : [material];

  materials.forEach((sourceMaterial) => {
    if (!sourceMaterial || !sourceMaterial.map) {
      return;
    }

    sourceMaterial.map.wrapS = window.THREE.RepeatWrapping;
    sourceMaterial.map.wrapT = window.THREE.RepeatWrapping;
    sourceMaterial.map.needsUpdate = true;
    sourceMaterial.needsUpdate = true;
  });
}

function getTargetProjectionOnSegment(point, segment) {
  const relative = {
    x: point.x - segment.targetFrom.x,
    z: point.z - segment.targetFrom.z,
  };
  const distanceAlongSegment = (relative.x * segment.targetDirection.x) + (relative.z * segment.targetDirection.z);
  const clampedDistance = Math.max(0, Math.min(segment.targetLength, distanceAlongSegment));
  const normalOffset = (relative.x * segment.targetNormal.x) + (relative.z * segment.targetNormal.z);
  const distanceFromSegment = Math.sqrt(
    Math.pow(relative.x - (segment.targetDirection.x * clampedDistance), 2)
    + Math.pow(relative.z - (segment.targetDirection.z * clampedDistance), 2)
  );

  return {
    clampedDistance,
    distanceFromSegment,
    normalOffset,
  };
}

function findClosestTargetSegment(point, segments) {
  return segments.reduce((closest, segment) => {
    const projection = getTargetProjectionOnSegment(point, segment);

    if (!closest || projection.distanceFromSegment < closest.projection.distanceFromSegment) {
      return {
        segment,
        projection,
      };
    }

    return closest;
  }, null);
}

// Collects per-vertex distance along the door front and vertical height for center-panel detection/projection.
function getDoorProjectionData(mesh, instance, segments, options = {}) {
  const THREE = window.THREE;
  const position = mesh.geometry && mesh.geometry.attributes && mesh.geometry.attributes.position;

  if (!position) {
    return null;
  }

  const vertex = new THREE.Vector3();
  const projections = [];
  const bounds = {
    minDistance: Infinity,
    maxDistance: -Infinity,
    minY: Infinity,
    maxY: -Infinity,
  };

  for (let vertexIndex = 0; vertexIndex < position.count; vertexIndex += 1) {
    if (options.positions) {
      const positionIndex = vertexIndex * 3;
      vertex.set(
        options.positions[positionIndex],
        options.positions[positionIndex + 1],
        options.positions[positionIndex + 2]
      );
    } else {
      vertex.fromBufferAttribute(position, vertexIndex);
    }

    mesh.localToWorld(vertex);
    instance.worldToLocal(vertex);

    const closest = options.useSourceSegments
      ? findClosestSegment({ x: vertex.x, z: vertex.z }, segments)
      : findClosestTargetSegment({ x: vertex.x, z: vertex.z }, segments);
    const distance = closest ? closest.projection.clampedDistance : 0;

    projections[vertexIndex] = {
      distance,
      y: vertex.y,
    };
    bounds.minDistance = Math.min(bounds.minDistance, distance);
    bounds.maxDistance = Math.max(bounds.maxDistance, distance);
    bounds.minY = Math.min(bounds.minY, vertex.y);
    bounds.maxY = Math.max(bounds.maxY, vertex.y);
  }

  return {
    projections,
    bounds,
  };
}

function getProjectionBoundsRatio(projection, bounds) {
  const distanceSize = bounds.maxDistance - bounds.minDistance;
  const height = bounds.maxY - bounds.minY;

  if (distanceSize <= 0 || height <= 0) {
    return null;
  }

  return {
    u: (projection.distance - bounds.minDistance) / distanceSize,
    y: (projection.y - bounds.minY) / height,
  };
}

function isCenterDoorPanelRatio(ratio) {
  if (!ratio) {
    return false;
  }

  return (
    ratio.u >= DOOR_CENTER_PANEL_MIN_U
    && ratio.u <= DOOR_CENTER_PANEL_MAX_U
    && ratio.y >= DOOR_CENTER_PANEL_MIN_Y
    && ratio.y <= DOOR_CENTER_PANEL_MAX_Y
  );
}

function getTriangleVertexIndex(indexAttribute, index, triangleOffset) {
  if (indexAttribute && indexAttribute.array) {
    return indexAttribute.array[(index * 3) + triangleOffset];
  }

  return (index * 3) + triangleOffset;
}

function getTriangleCenterProjection(projections, vertexIndexes) {
  return {
    distance: vertexIndexes.reduce((sum, vertexIndex) => sum + projections[vertexIndex].distance, 0) / vertexIndexes.length,
    y: vertexIndexes.reduce((sum, vertexIndex) => sum + projections[vertexIndex].y, 0) / vertexIndexes.length,
  };
}

function setDoorCenterPanelUv(uvArray, vertexIndex, projection, targetBounds, sourceSize) {
  const projectedU = (projection.distance - targetBounds.minDistance)
    / (sourceSize.distance * DOOR_CENTER_PANEL_TEXTURE_SCALE);
  const projectedV = (projection.y - targetBounds.minY) / sourceSize.height;
  const uvIndex = vertexIndex * 2;

  if (DOOR_CENTER_PANEL_ROTATE_TEXTURE) {
    uvArray[uvIndex] = projectedV;
    uvArray[uvIndex + 1] = projectedU;
    return;
  }

  uvArray[uvIndex] = projectedU;
  uvArray[uvIndex + 1] = projectedV;
}

// Operates at triangle level because center-panel quads often have vertices on the surrounding frame boundary.
function projectDoorCenterPanelTriangles(mesh, position, uv, projectionData, sourceSize) {
  const indexAttribute = mesh.geometry.index;
  const triangleCount = mesh.geometry.index
    ? Math.floor(mesh.geometry.index.array.length / 3)
    : Math.floor(position.count / 3);

  for (let triangleIndex = 0; triangleIndex < triangleCount; triangleIndex += 1) {
    const vertexIndexes = [
      getTriangleVertexIndex(indexAttribute, triangleIndex, 0),
      getTriangleVertexIndex(indexAttribute, triangleIndex, 1),
      getTriangleVertexIndex(indexAttribute, triangleIndex, 2),
    ];
    const centerProjection = getTriangleCenterProjection(projectionData.projections, vertexIndexes);
    const centerRatio = getProjectionBoundsRatio(centerProjection, projectionData.bounds);

    if (!isCenterDoorPanelRatio(centerRatio)) {
      continue;
    }

    vertexIndexes.forEach((vertexIndex) => {
      setDoorCenterPanelUv(
        uv.array,
        vertexIndex,
        projectionData.projections[vertexIndex],
        projectionData.bounds,
        sourceSize
      );
    });
  }
}

// Reprojects only the central panel of door meshes. Full-door projection breaks frame/profile authored UVs.
function projectDoorCenterPanelUv(mesh, instance, segments, options = {}) {
  if (!DOOR_MESH_NAME.test(mesh.name || '')) {
    return;
  }

  const material = getPrimaryMaterial(mesh.material);
  const position = mesh.geometry && mesh.geometry.attributes && mesh.geometry.attributes.position;
  const uv = mesh.geometry && mesh.geometry.attributes && mesh.geometry.attributes.uv;

  if (!material || !material.map || !position || !uv || !uv.array) {
    return;
  }

  const originalGeometry = recordOriginalGeometryPositions(mesh, options.geometryTag, null, null);
  const sourceProjectionData = originalGeometry
    ? getDoorProjectionData(mesh, instance, segments, {
      positions: originalGeometry.positions,
      useSourceSegments: true,
    })
    : null;
  const projectionData = getDoorProjectionData(mesh, instance, segments);

  if (!sourceProjectionData || !projectionData) {
    return;
  }

  const distanceSize = projectionData.bounds.maxDistance - projectionData.bounds.minDistance;
  const height = projectionData.bounds.maxY - projectionData.bounds.minY;
  const sourceSize = {
    distance: sourceProjectionData.bounds.maxDistance - sourceProjectionData.bounds.minDistance,
    height: sourceProjectionData.bounds.maxY - sourceProjectionData.bounds.minY,
  };

  if (distanceSize <= 0 || height <= 0 || sourceSize.distance <= 0 || sourceSize.height <= 0) {
    return;
  }

  projectDoorCenterPanelTriangles(mesh, position, uv, projectionData, sourceSize);

  uv.needsUpdate = true;
  updateTextureWrapping(mesh.material);
}

// Applies front-line 9-slice to authored door/drawer meshes after the general cabinet resize.
export function resizeFrontLineNineSlice(instance, dimensions, modelBounds, options = {}) {
  if (!instance || !modelBounds) {
    return;
  }

  const resizeOptions = {
    geometryTag: options.geometryTag || 'frontLineNineSliceOriginalGeometry',
    sideBandRatio: options.sideBandRatio || DEFAULT_FRONT_SIDE_BAND_RATIO || DEFAULT_SIDE_BAND_RATIO,
    frontMeshName: options.frontMeshName || DEFAULT_FRONT_MESH_NAME,
  };
  const segments = buildFrontSegments(
    dimensions.frontLineIndexes,
    dimensions.originalPoints,
    dimensions.currentPoints,
    modelBounds
  );
  const frontMeshes = [];

  if (!segments.length) {
    return;
  }

  instance.traverse((child) => {
    if (!child.isMesh || isRuntimeCabinetHandle(child)) {
      return;
    }

    if (isFrontMesh(child, resizeOptions.frontMeshName)) {
      frontMeshes.push(child);
    }
  });

  frontMeshes.forEach((child) => {
    if (child.userData[DEFAULT_INVISIBLE_MESH_TAG] && !child.visible) {
      return;
    }

    child.visible = true;
    resizeFrontMesh(child, instance, segments, resizeOptions);
    projectDoorCenterPanelUv(child, instance, segments, resizeOptions);
  });
}
